import { AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";

interface StockBarPreviewProps {
  settings: {
    totalStock?: number;
    soldStock?: number;
    showPercentage?: boolean;
    showWarning?: boolean;
    warningText?: string;
    barColor?: string;
    backgroundColor?: string;
    textColor?: string;
    padding?: string;
    borderRadius?: string;
    autoDecrease?: boolean;
    decreaseInterval?: number;
    decreaseAmount?: number;
  };
}

export const StockBarPreview = ({ settings }: StockBarPreviewProps) => {
  const total = settings.totalStock || 100;
  const [currentRemaining, setCurrentRemaining] = useState(total - (settings.soldStock || 75));

  useEffect(() => {
    if (!settings.autoDecrease) {
      setCurrentRemaining(total - (settings.soldStock || 75));
      return;
    }

    const interval = (settings.decreaseInterval || 5) * 1000;
    const amount = settings.decreaseAmount || 1;

    const timer = setInterval(() => {
      setCurrentRemaining((prev) => {
        const newRemaining = prev - amount;
        return newRemaining > 0 ? newRemaining : 1;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [settings.autoDecrease, settings.decreaseInterval, settings.decreaseAmount, total, settings.soldStock]);

  const sold = total - currentRemaining;
  const percentage = Math.round((sold / total) * 100);

  return (
    <div
      className="space-y-3"
      style={{
        padding: `${settings.padding || 16}px`,
      }}
    >
      {settings.showWarning && currentRemaining < total * 0.3 && (
        <div className="flex items-center gap-2 text-orange-600 animate-pulse">
          <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="font-semibold text-sm sm:text-base">
            {settings.warningText?.replace('${remaining}', currentRemaining.toString()) || `⚠️ تبقى فقط ${currentRemaining} قطعة!`}
          </span>
        </div>
      )}
      
      <div className="space-y-2">
        <div className="flex justify-between text-xs sm:text-sm font-medium" style={{ color: settings.textColor || "#000" }}>
          <span>تم بيع {sold} من {total}</span>
          {settings.showPercentage && <span className="font-bold">{percentage}% مباع</span>}
        </div>
        <div
          className="w-full h-3 sm:h-4 rounded-full overflow-hidden shadow-inner"
          style={{ backgroundColor: settings.backgroundColor || "#e5e7eb" }}
        >
          <div
            className="h-full transition-all duration-700 ease-out"
            style={{
              width: `${percentage}%`,
              backgroundColor: settings.barColor || "#10b981",
            }}
          />
        </div>
        <p className="text-xs text-center font-semibold" style={{ color: settings.textColor || "#000" }}>
          متبقي فقط {currentRemaining} قطعة
        </p>
      </div>
    </div>
  );
};
